import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as cornerstone from 'cornerstone-core';
import * as cornerstoneMath from 'cornerstone-math';
import * as dicomParser from 'dicom-parser';
import * as cornerstoneTools from 'cornerstone-tools';
import Hammer from 'hammerjs';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { Button, LoadIndicator, Popup, Tooltip } from 'devextreme-react';
import './DicomViewer.scss';
import { ruler, rotate90, invertColors, restore, handMove } from '../../../images/icons';
// --- Interfaces ---
interface DicomMetadata {
  patientName: string;
  studyDate: string;
  pixelSpacing: string;
  hasCalibration: boolean;
}

interface ViewportInfo {
  ww: number;
  wc: number;
  zoom: number;
}

type DicomTool = 'Wwwc' | 'Zoom' | 'Pan' | 'Rotate' | 'Length' | 'Angle' | 'Magnify' | 'Eraser';

interface DicomViewerProps {
  imageIds: string[];
}

// --- Configuración de dependencias ---
(cornerstoneWADOImageLoader as any).external.cornerstone = cornerstone;
(cornerstoneWADOImageLoader as any).external.dicomParser = dicomParser;
(cornerstoneTools as any).external.cornerstone = cornerstone;
(cornerstoneTools as any).external.cornerstoneMath = cornerstoneMath;
(cornerstoneTools as any).external.Hammer = Hammer;

cornerstoneTools.init();

// Store personalizado para pixel spacing por imageId.
// LengthTool consulta cornerstone.metaData.get('imagePlaneModule', imageId)
// como fuente PRIMARIA de pixel spacing. El provider de WADO-URI retorna
// el módulo pero sin rowPixelSpacing/columnPixelSpacing, causando que las
// mediciones caigan en píxeles. Este provider con prioridad alta lo corrige.
const pixelSpacingStore: Record<string, { rowPixelSpacing: number; columnPixelSpacing: number }> = {};

cornerstone.metaData.addProvider((type: string, imageId: string) => {
  if (type === 'imagePlaneModule' && pixelSpacingStore[imageId]) {
    return {
      ...pixelSpacingStore[imageId],
    };
  }
}, 10000); // prioridad alta para que se consulte ANTES que el provider de WADO-URI

// Registrar el metadata provider de WADO-URI como fallback para otros módulos
try {
  const wadoUriMetaData = (cornerstoneWADOImageLoader as any).wadouri?.metaData;
  if (wadoUriMetaData?.provider) {
    cornerstone.metaData.addProvider(wadoUriMetaData.provider);
  }
} catch { /* provider no disponible */ }

// Configurar colores de las herramientas de anotación:
// - Herramienta activa (mientras se dibuja): amarillo brillante
// - Anotación completada: verde lima (visible sobre fondo oscuro)
try {
  (cornerstoneTools as any).toolColors.setToolColor('#39FF14');
  (cornerstoneTools as any).toolColors.setActiveColor('#FFD700');
  (cornerstoneTools as any).toolStyle.setToolWidth(2);
} catch { /* versión de cornerstone-tools sin estos métodos */ }

// Función auxiliar: extraer pixel spacing de un dataSet DICOM
function extractPixelSpacing(dataSet: any): { row: number; col: number; source: string } | null {
  const tagsToTry: Array<{ tag: string; source: string }> = [
    { tag: 'x00280030', source: '' },           // Pixel Spacing (0028,0030)
    { tag: 'x00181164', source: ' (imager)' },   // Imager Pixel Spacing (0018,1164)
    { tag: 'x00182010', source: ' (nominal)' },  // Nominal Scanned Pixel Spacing (0018,2010)
  ];

  for (const { tag, source } of tagsToTry) {
    const value = dataSet.string(tag) || '';
    if (value) {
      const parts = value.split('\\');
      if (parts.length >= 2) {
        const row = parseFloat(parts[0]);
        const col = parseFloat(parts[1]);
        if (row > 0 && col > 0) {
          return { row, col, source };
        }
      }
    }
    // Intentar también con floatString
    try {
      const row = dataSet.floatString(tag, 0);
      const col = dataSet.floatString(tag, 1);
      if (row > 0 && col > 0) {
        return { row, col, source };
      }
    } catch { /* no disponible */ }
  }
  return null;
}

// Registro de herramientas (solo una vez)
const toolsRegistered = { current: false };

const DicomViewer: React.FC<DicomViewerProps> = ({ imageIds }) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const fullscreenViewerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageLoadedRef = useRef(false);
  const currentImageRef = useRef<cornerstone.Image | null>(null);
  const [metadata, setMetadata] = useState<DicomMetadata>({ patientName: '', studyDate: '', pixelSpacing: '', hasCalibration: false });
  const [viewportInfo, setViewportInfo] = useState<ViewportInfo>({ ww: 0, wc: 0, zoom: 1 });
  const [activeTool, setActiveTool] = useState<DicomTool>('Wwwc');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [isInverted, setIsInverted] = useState(false);
  const [isHFlipped, setIsHFlipped] = useState(false);
  const [isVFlipped, setIsVFlipped] = useState(false);
  const isFullscreenRef = useRef(false);

  // Navegación multi-imagen
  const [currentIndex, setCurrentIndex] = useState(0);

  // Asegurar que el índice sea válido cuando cambia el array
  useEffect(() => {
    setCurrentIndex((prev) => {
      if (imageIds.length === 0) return 0;
      if (prev >= imageIds.length) return imageIds.length - 1;
      return prev;
    });
  }, [imageIds.length]);

  const currentImageId = imageIds[currentIndex] || '';

  const handlePrevImage = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextImage = useCallback(() => {
    setCurrentIndex((prev) => Math.min(imageIds.length - 1, prev + 1));
  }, [imageIds.length]);

  // Mantener ref sincronizada con el estado
  useEffect(() => {
    isFullscreenRef.current = isFullscreen;
  }, [isFullscreen]);

  // Obtener el elemento cornerstone activo (inline o fullscreen)
  const getActiveElement = useCallback((): HTMLDivElement | null => {
    if (isFullscreenRef.current && fullscreenViewerRef.current) {
      return fullscreenViewerRef.current;
    }
    return viewerRef.current;
  }, []);

  // Actualizar info del viewport
  const updateViewportInfo = useCallback(() => {
    const element = getActiveElement();
    if (!element) return;
    try {
      const vp = cornerstone.getViewport(element);
      if (vp) {
        setViewportInfo({
          ww: Math.round(vp.voi.windowWidth),
          wc: Math.round(vp.voi.windowCenter),
          zoom: Math.round(vp.scale * 100) / 100,
        });
      }
    } catch { /* elemento no habilitado */ }
  }, [getActiveElement]);

  // Registrar todas las herramientas al elemento
  const registerTools = useCallback((element: HTMLDivElement) => {
    const tools: any[] = [
      cornerstoneTools.WwwcTool,
      cornerstoneTools.ZoomTool,
      cornerstoneTools.PanTool,
      cornerstoneTools.RotateTool,
      cornerstoneTools.LengthTool,
      cornerstoneTools.AngleTool,
      cornerstoneTools.MagnifyTool,
      cornerstoneTools.EraserTool,
    ];
    tools.forEach((tool) => {
      try {
        cornerstoneTools.addToolForElement(element, tool);
      } catch { /* ya registrada */ }
    });

    // Activar Wwwc por defecto
    cornerstoneTools.setToolActiveForElement(element, 'Wwwc', { mouseButtonMask: 1 });

    // Zoom con rueda del mouse
    try {
      cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomMouseWheelTool);
      cornerstoneTools.setToolActiveForElement(element, 'ZoomMouseWheel', {});
    } catch { /* ya registrada */ }
  }, []);

  // Cambiar herramienta activa
  const handleSetTool = useCallback((toolName: DicomTool) => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;

    // Desactivar herramientas previas (poner en enabled)
    const allTools: DicomTool[] = ['Wwwc', 'Zoom', 'Pan', 'Rotate', 'Length', 'Angle', 'Magnify', 'Eraser'];
    allTools.forEach((t) => {
      try {
        cornerstoneTools.setToolEnabledForElement(element, t);
      } catch { /* ignorar */ }
    });

    // Activar la seleccionada
    cornerstoneTools.setToolActiveForElement(element, toolName, { mouseButtonMask: 1 });
    setActiveTool(toolName);
  }, [getActiveElement]);

  // Reset viewport
  const handleReset = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    cornerstone.reset(element);
    setIsInverted(false);
    setIsHFlipped(false);
    setIsVFlipped(false);
    updateViewportInfo();
  }, [getActiveElement, updateViewportInfo]);

  // Limpiar todas las anotaciones
  const handleClearAll = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    const toolsToClear = ['Length', 'Angle', 'EllipticalRoi', 'RectangleRoi', 'FreehandRoi'];
    toolsToClear.forEach((toolName) => {
      try {
        cornerstoneTools.clearToolState(element, toolName);
      } catch { /* no state */ }
    });
    cornerstone.updateImage(element);
  }, [getActiveElement]);

  // Invertir colores
  const handleInvert = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    const vp = cornerstone.getViewport(element);
    vp.invert = !vp.invert;
    cornerstone.setViewport(element, vp);
    setIsInverted((prev) => !prev);
  }, [getActiveElement]);

  // Flip horizontal
  const handleFlipH = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    const vp = cornerstone.getViewport(element);
    vp.hflip = !vp.hflip;
    cornerstone.setViewport(element, vp);
    setIsHFlipped((prev) => !prev);
  }, [getActiveElement]);

  // Flip vertical
  const handleFlipV = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    const vp = cornerstone.getViewport(element);
    vp.vflip = !vp.vflip;
    cornerstone.setViewport(element, vp);
    setIsVFlipped((prev) => !prev);
  }, [getActiveElement]);

  // Rotar 90°
  const handleRotate90 = useCallback(() => {
    const element = getActiveElement();
    if (!element || !imageLoadedRef.current) return;
    const vp = cornerstone.getViewport(element);
    vp.rotation = (vp.rotation || 0) + 90;
    cornerstone.setViewport(element, vp);
  }, [getActiveElement]);

  // Abrir visor en pantalla completa (Popup)
  const handleFullscreen = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  // Cerrar fullscreen
  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  // Cuando el popup se muestra, habilitar cornerstone en el contenedor fullscreen
  const handleFullscreenShown = useCallback(() => {
    const fsElement = fullscreenViewerRef.current;
    const image = currentImageRef.current;
    if (!fsElement || !image) return;

    cornerstone.enable(fsElement);
    cornerstone.displayImage(fsElement, image);
    cornerstone.fitToWindow(fsElement);
    registerTools(fsElement);

    // Sincronizar viewport actual
    try {
      const inlineVp = cornerstone.getViewport(viewerRef.current!);
      if (inlineVp) cornerstone.setViewport(fsElement, inlineVp);
    } catch { /* ignorar */ }

    // Activar la herramienta actual
    cornerstoneTools.setToolActiveForElement(fsElement, activeTool, { mouseButtonMask: 1 });

    fsElement.addEventListener('cornerstoneimagerendered', updateViewportInfo);
  }, [activeTool, registerTools, updateViewportInfo]);

  // Cleanup al cerrar popup y sincronizar herramienta activa al visor inline
  const handleFullscreenHidden = useCallback(() => {
    const fsElement = fullscreenViewerRef.current;
    if (fsElement) {
      fsElement.removeEventListener('cornerstoneimagerendered', updateViewportInfo);
      try { cornerstone.disable(fsElement); } catch { /* ya deshabilitado */ }
    }
    // Sincronizar la herramienta activa de vuelta al visor inline
    const inlineElement = viewerRef.current;
    if (inlineElement && imageLoadedRef.current) {
      const allTools: DicomTool[] = ['Wwwc', 'Zoom', 'Pan', 'Rotate', 'Length', 'Angle', 'Magnify', 'Eraser'];
      allTools.forEach((t) => {
        try { cornerstoneTools.setToolEnabledForElement(inlineElement, t); } catch { /* ignorar */ }
      });
      try { cornerstoneTools.setToolActiveForElement(inlineElement, activeTool, { mouseButtonMask: 1 }); } catch { /* ignorar */ }
    }
  }, [activeTool, updateViewportInfo]);

  // Cargar imagen DICOM
  useEffect(() => {
    const element = viewerRef.current;
    if (!element || !currentImageId || typeof currentImageId !== 'string') return;

    setIsLoading(true);
    setError('');
    imageLoadedRef.current = false;

    cornerstone.enable(element);

    const config = {
      webWorkerPath: `${process.env.PUBLIC_URL}/cornerstoneWADOImageLoaderWebWorker.js`,
      taskConfiguration: {
        decodeTask: {
          codecsPath: `${process.env.PUBLIC_URL}/cornerstoneWADOImageLoaderCodecs.js`,
        },
      },
    };
    (cornerstoneWADOImageLoader as any).webWorkerManager.initialize(config);

    const resolvedImageId = currentImageId.startsWith('wadouri:') ? currentImageId : `wadouri:${currentImageId}`;

    cornerstone
      .loadImage(resolvedImageId)
      .then((image: cornerstone.Image) => {
        currentImageRef.current = image;

        const dataSet = image.data;

        // === 1. Extraer Pixel Spacing ANTES de displayImage ===
        let rowPS: number = (image as any).rowPixelSpacing || 0;
        let colPS: number = (image as any).columnPixelSpacing || 0;
        let hasCalibration = rowPS > 0 && colPS > 0;
        let psSource = '';

        // Si el loader no lo estableció, extraer manualmente de los tags DICOM
        if (!hasCalibration && dataSet) {
          const spacing = extractPixelSpacing(dataSet);
          if (spacing) {
            rowPS = spacing.row;
            colPS = spacing.col;
            hasCalibration = true;
            psSource = spacing.source;
          }
        }

        // Inyectar pixel spacing en el objeto imagen
        if (hasCalibration) {
          (image as any).rowPixelSpacing = rowPS;
          (image as any).columnPixelSpacing = colPS;

          // Registrar en el metadata store para que LengthTool/AngleTool
          // lo obtengan vía cornerstone.metaData.get('imagePlaneModule')
          pixelSpacingStore[resolvedImageId] = {
            rowPixelSpacing: rowPS,
            columnPixelSpacing: colPS,
          };
        }

        const psDisplay = hasCalibration
          ? `${rowPS.toFixed(3)} × ${colPS.toFixed(3)} mm/px${psSource}`
          : '';

        setMetadata({
          patientName: dataSet?.string('x00100010') || 'Paciente Anónimo',
          studyDate: dataSet?.string('x00080020') || 'Fecha N/A',
          pixelSpacing: psDisplay,
          hasCalibration,
        });

        // === 2. Mostrar la imagen DESPUÉS de configurar pixel spacing ===
        cornerstone.displayImage(element, image);
        cornerstone.fitToWindow(element);

        // === 3. Si estamos en fullscreen, actualizar también el visor fullscreen ===
        const fsElement = fullscreenViewerRef.current;
        if (isFullscreenRef.current && fsElement) {
          try {
            cornerstone.displayImage(fsElement, image);
            cornerstone.fitToWindow(fsElement);
          } catch { /* el elemento fullscreen podría no estar habilitado */ }
        }

        // === 4. Registrar herramientas y actualizar ===
        registerTools(element);
        imageLoadedRef.current = true;
        updateViewportInfo();
        setIsLoading(false);

        // Listener para actualizar viewport info en tiempo real
        element.addEventListener('cornerstoneimagerendered', updateViewportInfo);
      })
      .catch((err: any) => {
        console.error('Error cargando DICOM:', err);
        setError('No se pudo cargar la imagen DICOM');
        setIsLoading(false);
      });

    return () => {
      if (element) {
        element.removeEventListener('cornerstoneimagerendered', updateViewportInfo);
        try { cornerstone.disable(element); } catch { /* ya deshabilitado */ }
      }
    };
  }, [currentImageId, registerTools, updateViewportInfo]);

  // Definición de botones del toolbar
  const toolButtons: { tool?: DicomTool; icon: string; hint: string; action?: () => void; isActive?: boolean }[] = [
    { tool: 'Wwwc', icon: 'sun', hint: 'Brillo / Contraste (WW/WC)' },
    { tool: 'Zoom', icon: 'search', hint: 'Zoom (también rueda del mouse)' },
    { tool: 'Pan', icon: handMove, hint: 'Mover imagen' },
    { tool: 'Rotate', icon: 'repeat', hint: 'Rotar libremente' },
    { hint: 'separator' } as any,
    { tool: 'Length', icon: ruler, hint: 'Medir distancia' },
    { tool: 'Angle', icon: 'chart', hint: 'Medir ángulo' },
    { tool: 'Magnify', icon: 'find', hint: 'Lupa' },
    { tool: 'Eraser', icon: 'clear', hint: 'Borrar anotación individual' },
    { hint: 'separator' } as any,
    { icon: rotate90, hint: 'Rotar 90°', action: handleRotate90 },
    { icon: invertColors, hint: 'Invertir colores', action: handleInvert, isActive: isInverted },
    { icon: 'alignright', hint: 'Voltear horizontal', action: handleFlipH, isActive: isHFlipped },
    { icon: 'arrowdown', hint: 'Voltear vertical', action: handleFlipV, isActive: isVFlipped },
    { hint: 'separator' } as any,
    { icon: 'trash', hint: 'Limpiar todas las anotaciones', action: handleClearAll },
    { icon: restore, hint: 'Restablecer vista', action: handleReset },
  ];

  // Función para renderizar el toolbar
  const renderToolbar = (prefix: string) => (
    <div className='dicom-toolbar'>
      {toolButtons.map((btn, idx) => {
        if (btn.hint === 'separator') {
          return <div key={`sep-${idx}`} className='dicom-toolbar-separator' />;
        }
        const isToolActive = btn.tool ? activeTool === btn.tool : btn.isActive;
        return (
          <React.Fragment key={btn.hint}>
            <Button
              id={`${prefix}-btn-${idx}`}
              icon={btn.icon}
              stylingMode='text'
              className={isToolActive ? 'dicom-tool-active' : ''}
              onClick={() => (btn.tool ? handleSetTool(btn.tool) : btn.action?.())}
              disabled={isLoading || !!error}
            />
            <Tooltip target={`#${prefix}-btn-${idx}`} showEvent='mouseenter' hideEvent='mouseleave' position='left'>
              {btn.hint}
            </Tooltip>
          </React.Fragment>
        );
      })}
    </div>
  );

  // Navegación entre múltiples imágenes
  const renderImageNav = (prefix: string) => {
    if (imageIds.length <= 1) return null;
    return (
      <div className='dicom-image-nav'>
        <Button
          id={`${prefix}-prev-img`}
          icon='chevronleft'
          stylingMode='text'
          onClick={handlePrevImage}
          disabled={currentIndex === 0 || isLoading}
        />
        <span className='dicom-image-counter'>
          {currentIndex + 1} / {imageIds.length}
        </span>
        <Button
          id={`${prefix}-next-img`}
          icon='chevronright'
          stylingMode='text'
          onClick={handleNextImage}
          disabled={currentIndex === imageIds.length - 1 || isLoading}
        />
      </div>
    );
  };

  // Función para renderizar los overlays
  const renderOverlays = () => (
    <>
      <div className='dicom-overlay-top-left'>
        <div className='dicom-patient-name'>{metadata.patientName}</div>
        <div className='dicom-study-date'>{metadata.studyDate}</div>
      </div>
      {!isLoading && !error && (
        <div className='dicom-overlay-bottom-left'>
          <div className='dicom-ww-wc'>WW: {viewportInfo.ww} / WC: {viewportInfo.wc}</div>
          <div className='dicom-zoom-level'>Zoom: {viewportInfo.zoom}x</div>
        </div>
      )}
      {!isLoading && !error && (
        <div className='dicom-overlay-bottom-right'>
          <div>{activeTool}</div>
          {metadata.hasCalibration ? (
            <div className='dicom-calibration calibrated'>📐 {metadata.pixelSpacing}</div>
          ) : (
            <div className='dicom-calibration not-calibrated'>⚠ Sin calibración (px)</div>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Visor inline */}
      <div ref={containerRef} className='dicom-viewer-container'>
        {renderToolbar('dicom')}
        {renderImageNav('dicom')}

        {/* Botón pantalla completa flotante arriba-derecha, fuera del toolbar */}
        <Button
          id='dicom-fullscreen-btn'
          icon='fullscreen'
          stylingMode='text'
          className='dicom-fullscreen-btn'
          hint='Pantalla completa'
          onClick={handleFullscreen}
          disabled={isLoading || !!error}
        />

        {renderOverlays()}

        {isLoading && (
          <div className='dicom-loading'>
            <LoadIndicator visible={true} height={40} width={40} />
            <span>Cargando imagen DX...</span>
          </div>
        )}

        {error && (
          <div className='dicom-error'>
            <i className='dx-icon dx-icon-warning' />
            <span>{error}</span>
          </div>
        )}

        <div ref={viewerRef} className='dicom-canvas' />
      </div>

      {/* Modal pantalla completa */}
      <Popup
        visible={isFullscreen}
        onHiding={handleCloseFullscreen}
        onShown={handleFullscreenShown}
        onHidden={handleFullscreenHidden}
        fullScreen={true}
        showCloseButton={true}
        showTitle={true}
        title='Visor DICOM - Pantalla Completa'
        dragEnabled={false}
        wrapperAttr={{ class: 'dicom-fullscreen-popup' }}
      >
        <div className='dicom-viewer-container dicom-viewer-fullscreen-content'>
          {renderToolbar('fs')}
          {renderImageNav('fs')}
          {renderOverlays()}
          <div ref={fullscreenViewerRef} className='dicom-canvas' />
        </div>
      </Popup>
    </>
  );
};

export default DicomViewer;
